import { 
  Box, 
  Divider, 
  HStack, 
  Icon, 
  IconButton, 
  Spinner,
  Text, 
  VStack,
  Image,
  useToast
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaCopy, FaThumbsDown, FaThumbsUp } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import ToolCall from "./ToolCall"; 
import { ToolCall as ToolCallType } from "build-ts/chat";
import { sendFeedback } from "../../services/ChatBotService";

interface ChatMessageProps {
  text: string;
  sender: "user" | "bot";
  isLoading: boolean;
  isLastMessage: boolean;
  toolCalls?: ToolCallType[] | undefined;
  execution_id?: string;
}

// Function to clean hyperlinks in the format <https://...> or <http://...>
const removeHyperlinks = (text: string): string => {
  // Remove hyperlinks in the format <https://...> or <http://...>
  // This pattern matches URLs wrapped in angle brackets
  return text.replace(/<https?:\/\/[^>]+>/g, "").trim();
};

const ChatMessage: React.FC<ChatMessageProps> = ({
  text,
  sender,
  isLoading,
  isLastMessage,
  toolCalls,
  execution_id
}) => {
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [loadingTime, setLoadingTime] = useState(0);
  const toast = useToast();

  useEffect(() => {
    if (isLoading && isLastMessage) {
      const interval = setInterval(() => {
        setLoadingTime(prevTime => prevTime + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isLoading, isLastMessage]);

  const handleLike = async () => {
    if (!execution_id) return;
    
    try {
      if (liked) {
        setLiked(false);
      } else {
        await sendFeedback(execution_id, "GOOD");
        setLiked(true);
        setDisliked(false);
      }
    } catch (error) {
      console.error("Error sending feedback:", error);
    }
  };

  const handleDislike = async () => {
    if (!execution_id) return;

    try {
      if (disliked) {
        setDisliked(false);
      } else {
        await sendFeedback(execution_id, "BAD");
        setDisliked(true);
        setLiked(false);
      }
    } catch (error) {
      console.error("Error sending feedback:", error);
    }
  };

  // function to copy text to clipboard 
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      
      // Show a toast notification instead of changing button state
      toast({
        title: "Copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top",
        variant: "subtle",
      });
    } catch (error) {
      console.error("Failed to copy text:", error);
      toast({
        title: "Failed to copy",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
    }
  };

  return (
    <HStack
      alignSelf={sender === "user" ? "flex-end" : "flex-start"} 
      w={sender === "user" ? "auto" : "95%"}
    >
      <Box
        bg={sender === "user" ? "purple.50" : "gray.50"}
        p={3}
        borderRadius="lg"
        maxW="100%"
        flex="1"
        ml={sender === "user" ? "auto" : "0"}
        mb={0}
      >
        {isLoading && isLastMessage ? (
          // Loading state
          <VStack align="start" spacing={2}>
            <Box display="flex" alignItems="center" minH="24px">
              <Spinner size="sm" color="purple.700" mr={2} />
              <Text fontSize="sm" color="gray.500" mb={0}>
                Thinking...
              </Text>
            </Box>
            {toolCalls && toolCalls.length > 0 && (
              <ToolCall 
                showProgress={true}
                toolCalls={toolCalls}
              />
            )}
          </VStack>
        ) : (
          // Normal message state
          <VStack align="start" spacing={1} w="100%">
            { sender === "bot" && (
              <VStack w="100%">
                <HStack w="100%" justify="space-between">
                  <HStack>
                        <Image 
                         src="/Anko.png"
                         alt="Anko"
                         boxSize={6}
                         mr={2}
                         objectFit="contain"
                         sx={{
                           "@keyframes spin": {
                             "0%": { transform: "rotate(0deg)" },
                             "50%": { transform: "rotate(360deg)" },
                             "100%": { transform: "rotate(360deg)" },
                           },
                         }}
                         _groupHover={{
                           animation: "spin 4s linear infinite",
                         }}
                       />
                    {loadingTime > 0 && (
                      <Text 
                        fontSize="xs" 
                        color="gray.600" 
                        mb={0} 
                        fontWeight={500} 
                        textAlign="left"
                      >
                        Worked for {loadingTime} second{loadingTime !== 1 ? "s" : ""}
                      </Text>
                    )}
                  </HStack>
                  <HStack spacing={0}>
                  <IconButton
                    icon={<Icon as={FaCopy} boxSize={3} />}
                    aria-label="Copy text"
                    size="2xs"
                    variant="ghost"
                    color="gray.300"
                    onClick={handleCopy}
                    _hover={{color: "gray.500"}}
                    title="Copy to clipboard"
                  />
                  <IconButton
                    icon={<Icon as={FaThumbsUp} boxSize={3} />}
                    aria-label="Like"
                    size="2xs"
                    variant="ghost"
                    color={liked ? "green.500" : "gray.300"}
                    onClick={handleLike}
                    _hover={{color: liked ? "green.400" : "gray.500"}}
                  />
                  <IconButton
                    icon={<Icon as={FaThumbsDown} boxSize={3} />}
                    aria-label="Dislike"
                    size="2xs"
                    variant="ghost"
                    color={disliked ? "red.500" : "gray.300"}
                    onClick={handleDislike}
                    _hover={{color: disliked ? "red.400" : "gray.500"}}
                  />
                </HStack>
                </HStack>
                <Divider my={0} borderColor="gray.300" />
              </VStack>
            )}
            { sender === "bot" && (
              <VStack align="start" spacing={1} w="100%">
                {toolCalls && toolCalls.length > 0 && (
                  <ToolCall 
                    showProgress={false}
                    toolCalls={toolCalls}
                  />
                )}
                <Box textAlign="left" ml={0} className="markdown-content">
                  <ReactMarkdown
                    components={{
                      // Add custom components for markdown elements
                      ul: ({node, ...props}) => (
                        <ul style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
                      ),
                      ol: ({node, ...props}) => (
                        <ol style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
                      ),
                      li: ({node, ...props}) => (
                        <li style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }} {...props} />
                      ),
                      p: ({node, ...props}) => (
                        <p style={{ fontSize: "0.875rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
                      ),
                      h1: ({node, ...props}) => (
                        <h1 style={{ fontSize: "1.15rem", fontWeight: "bold", margin: "1rem 0" }} {...props} />
                      ),
                      h2: ({node, ...props}) => (
                        <h2 style={{ fontSize: "1.05rem", fontWeight: "bold", margin: "0.875rem 0" }} {...props} />
                      ),
                      h3: ({node, ...props}) => (
                        <h3 style={{ fontSize: "0.95rem", fontWeight: "bold", margin: "0.75rem 0" }} {...props} />
                      ),
                      h4: ({node, ...props}) => (
                        <h4 style={{ fontSize: "0.85rem", fontWeight: "bold", margin: "0.625rem 0" }} {...props} />
                      ),
                    }}
                  >
                    {removeHyperlinks(text)}
                  </ReactMarkdown>
                </Box>
              </VStack>
            )}
            { sender === "user" && (
              <Text mb={0} alignSelf="flex-end" fontSize="sm" textAlign="left">
                  {text }
              </Text>
            )}
          </VStack>
        )}
      </Box>
    </HStack>
  );
};

export default ChatMessage;